

import Vue from 'vue'
import Component, { mixins } from "vue-class-component";
import { PingStatusMixin } from "@/helpers/PingStatusMixin";
import {Watch} from "vue-property-decorator";
import {namespace} from "vuex-class";
import {ELockerStatuses} from "@/model/Locker/lockerInterfaces";
import ShowDeletedButton from "@/components/shared/ShowDeletedButton.vue";
import LockerChip from "@/components/shared/chips/LockerChip.vue";
import {IOnOffHandlers} from "bonomo/dist";
import {IShortLocker} from "@/model/shortInterfaces";
import LockerStatusChip from "@/components/shared/chips/LockerStatusChip.vue";
import {BetterDataTableHeader} from "@/model/betterVuetifyInterfaces";
import {UrlHelper} from "@/helpers/UrlHelper";
import CreateButton from "@/components/shared/CreateButton.vue";
import PaginationBar from "@/components/shared/PaginationBar.vue";
import CsvDownloader from "@/components/shared/CsvDownloader.vue";
import { lockerService } from "@/services/lockerService";
import { IAuthUserData } from "@/store/modules/authModule";
import { PermissionHelper } from "@/helpers/PermissionHelper";
import { EPermissionActions } from "@/model/EPermissionActions";
import { ILockerNetworkHealth } from "@/model/shortInterfaces";

const lockerModule = namespace("lockerModule");
const authModule = namespace("authModule");

  @Component({
    components: {CreateButton, LockerStatusChip, LockerChip, ShowDeletedButton, PaginationBar, CsvDownloader}
  })
  export default class InstallList extends mixins(PingStatusMixin) {

    @lockerModule.Getter('lockersList')
    lockersList!: IShortLocker[];

    @lockerModule.Getter('lockersListCurrentPage')
    lockersListCurrentPage!: number;

    @lockerModule.Getter('lockersListLastPage')
    lockersListLastPage!: number;

    @lockerModule.Getter('lockersListTotal')
    lockersListTotal!: number;

    @authModule.Getter('userData')
    userData!: IAuthUserData;

    @authModule.Getter('canAction')
    canAction!: (action: string) => boolean;

    get isAdmin(): boolean {
      const role: any = this.userData && this.userData.role;
      return PermissionHelper.hasRoleAdministrator(this.userData) || role === 10 || role === '10';
    }

    get createLockerUrl(): string {
      return '/lockers/create';
    }

    get canCreateLocker(): boolean {
      return this.canAction(EPermissionActions.ACTION_LOCKERS_CREATE);
    }
    get headers(): BetterDataTableHeader[] {
      return [
        { text: this.$t('common.id'), align: 'start', sortable: true, value: 'id' },
        { text: this.$t('common.name'), align: 'start', sortable: true, value: 'name' },
        { text: this.$t('common.community'), align: 'start', sortable: true, value: 'community' },
        { text: this.$t('lockers.lockers.fields.machine'), align: 'center', sortable: true, value: 'machine_number' },
        { text: this.$t('lockers.lockers.fields.version'), align: 'start', sortable: true, value: 'version' },
        { text: this.$t('lockers.lockers.fields.os_version'), align: 'center', sortable: true, value: 'os_version' },
        { text: this.$t('lockers.lockers.fields.os_memory'), align: 'center', sortable: true, value: 'os_cpu' },
        { text: this.$t('common.status'), align: 'start', sortable: true, value: 'status' },
        { text: this.$t('lockers.lockers.fields.counts.title'), align: 'center', sortable: false, value: 'counts' },
        { text: this.$t('lockers.lockers.fields.last_sync'), align: 'start', sortable: true, value: 'last_ping' },
        { text: 'Internet', align: 'center', sortable: false, value: 'network_health' },
        { text: '', sortable: false, value: 'detailButton' },
      ];
    }
    get maxVersion(): string {
      let maxVersion = '';
      this.lockersList.forEach((each: IShortLocker) => {
        if (this.isNewerVersion(each.version.split('.'), maxVersion.split('.'))) {
          maxVersion = each.version;
        }
      });
      return maxVersion;
    }
    get oldUrl(): string {
      return UrlHelper.mayorLockers() + '?stay';
    }

    get hasActiveFilters(): boolean {
      return !!this.filterStatus || !!this.filterOs;
    }

    get activeFilterCount(): number {
      return (this.filterStatus ? 1 : 0) + (this.filterOs ? 1 : 0);
    }

    get statusOptions(): Array<{value: string, label: string, activeColor: string, inactiveColor: string, inactiveTextColor: string}> {
      const colorMap: any = {
        [ELockerStatuses.Online]:  { label: 'Online',   activeColor: 'green darken-2',  inactiveColor: 'green lighten-4',  inactiveTextColor: 'green darken-3' },
        [ELockerStatuses.Off]:     { label: 'Offline',  activeColor: 'red darken-2',    inactiveColor: 'red lighten-4',    inactiveTextColor: 'red darken-3' },
        [ELockerStatuses.Install]: { label: 'Install',  activeColor: 'orange darken-2', inactiveColor: 'orange lighten-4', inactiveTextColor: 'orange darken-3' },
      };
      return this.filterOptionsStatuses.map((s: string) => {
        const cfg = colorMap[s] || {};
        return {
          value: s,
          label: cfg.label || s,
          activeColor: cfg.activeColor || 'blue darken-2',
          inactiveColor: cfg.inactiveColor || 'blue lighten-4',
          inactiveTextColor: cfg.inactiveTextColor || 'blue darken-3',
        };
      });
    }

    get osOptions(): Array<{value: string, label: string}> {
      return this.filterOptionsOsVersions.map((v: string) => ({
        value: v,
        label: this.osVersionToHuman(v),
      }));
    }

    loading: boolean = false;
    showAll: boolean = false;
    search: string = '';
    networkHealthMap: Record<number, ILockerNetworkHealth> = {};
    filterStatus: string = '';
    filterOs: string = '';
    filtersExpanded: boolean = false;
    filterOptionsStatuses: string[] = [];
    filterOptionsOsVersions: string[] = [];
    subscribes: IOnOffHandlers = {};
    private pollInterval: any = null;
    private networkHealthPollInterval: any = null;
    private selectedPerPage: number = 15;
    readonly perPageOptions: number[] = [15, 20, 30, 50, 100];
    private searchTimer: any = null;
    private sortTimer: any = null;
    private sortBy: string | string[] = 'id';
    private sortDesc: boolean | boolean[] = false;

    created() {
      this.subscribes = this.$ons({
        'login.OK': this.loadData,
        'backend.call.OK': () => { this.loading = false; },
      });
      this.loadData();
      lockerService.fetchInstallFilterOptions().then((opts) => {
        this.filterOptionsStatuses = opts.statuses;
        this.filterOptionsOsVersions = opts.os_versions;
      });
      lockerService.fetchNetworkHealth().then((data: Record<number, ILockerNetworkHealth>) => {
        this.networkHealthMap = data;
      });
      this.pollInterval = setInterval(this.silentFetch, 60000);
      this.networkHealthPollInterval = setInterval(this.silentFetchNetworkHealth, 30000);
    }
    beforeDestroy() {
      this.$offs(this.subscribes);
      clearInterval(this.pollInterval);
      clearInterval(this.networkHealthPollInterval);
    }

    loadData() {
      this.loading = true;
      this.$store.dispatch('lockerModule/clearLockersList');
      this.$store.dispatch('lockerModule/fetchLockersList', this.buildFilters());
    }

    private buildFilters(): any {
      const filters: any = { page: 1, per_page: this.selectedPerPage };
      if (this.search) {
        const dmMatch = this.search.match(/^DM-0*(\d+)$/i);
        if (dmMatch) {
          filters.machine_number = parseInt(dmMatch[1], 10);
        } else {
          filters.search = this.search;
        }
      }
      if (this.showAll) filters.show_deleted = 1;
      if (this.filterStatus) filters.status = this.filterStatus;
      if (this.filterOs) filters.os_filter = this.filterOs;
      filters.sort_by = this.activeSortBy;
      filters.sort_order = this.activeSortOrder;
      return filters;
    }

    private reload() {
      this.loading = true;
      this.$store.dispatch('lockerModule/clearLockersList');
      this.$store.dispatch('lockerModule/fetchLockersList', this.buildFilters());
    }

    onPageChange(page: number) {
      this.loading = true;
      this.$store.dispatch('lockerModule/clearLockersList');
      const filters = this.buildFilters();
      filters.page = page;
      this.$store.dispatch('lockerModule/fetchLockersList', filters);
    }

    onPerPageChange(value: number) {
      this.selectedPerPage = value;
      this.reload();
    }

    onStatusChipClick(value: string) {
      this.filterStatus = this.filterStatus === value ? '' : value;
      this.reload();
    }

    onOsChipClick(value: string) {
      this.filterOs = this.filterOs === value ? '' : value;
      this.reload();
    }

    @Watch('search')
    onSearchChange() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.reload();
      }, 400);
    }

    @Watch('showAll')
    onShowAllChange() {
      this.reload();
    }

    @Watch('sortBy')
    onSortByChange() {
      this.scheduleSortReload();
    }

    @Watch('sortDesc')
    onSortDescChange() {
      this.scheduleSortReload();
    }

    private scheduleSortReload() {
      clearTimeout(this.sortTimer);
      this.sortTimer = setTimeout(() => {
        this.reload();
      }, 0);
    }

    private get activeSortBy(): string {
      const sortBy = Array.isArray(this.sortBy) ? this.sortBy[0] : this.sortBy;
      return sortBy || 'id';
    }

    private get activeSortOrder(): string {
      const sortDesc = Array.isArray(this.sortDesc) ? this.sortDesc[0] : this.sortDesc;
      return sortDesc ? 'desc' : 'asc';
    }

    convertValueToHuman(fileSizeInBytes: string) {
      let fileSizeNumber: number = parseInt(fileSizeInBytes);
      let i = -1;
      const byteUnits = ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
      do {
        fileSizeNumber /= 1024;
        i++;
      } while (fileSizeNumber > 1024);

      return Math.max(fileSizeNumber, 0.1).toFixed(1) + byteUnits[i];
    }

    isMaxVersion(item: IShortLocker): boolean {
      return item.version == this.maxVersion;
    }

    isNewerVersion(a: string[], b: string[]): boolean {
      if (!a.length) {
        return false;
      }
      else if (!b.length) {
        return true;
      }
      else if (Number(a[0]) > Number(b[0])) {
        return true;
      }
      else if (Number(b[0]) > Number(a[0])) {
        return false;
      }
      return this.isNewerVersion(a.slice(1), b.slice(1));
    }

    osCpuToHuman(osCpu: string) {
      const regexp = /(Core\(TM\) i\d-\d+[^ ]*)|(celeron\(R\) [^ ]*\d+[^ ]*)/ig
      const matches = osCpu.match(regexp);
      return matches ? matches[0] : osCpu;
    }

    osVersionToHuman(osVersion: string) {
      let regexp = /^((Debian) GNU\/Linux (\d+))$/
      let matches = osVersion.match(regexp);
      if (matches) {
        return (matches[2] + ' ' + matches[3])
      }
      regexp = /^((LMDE) (\d+) .+)$/
      matches = osVersion.match(regexp);
      if (matches) {
        return (matches[2] + ' ' + matches[3])
      }
      return osVersion;
    }

    get csvHeaders(): string[] {
      return ['id', 'name', 'community', 'machine_number', 'version', 'os_version', 'os_cpu', 'os_memory', 'status', 'sp_count', 'screensaver_count', 'last_ping'];
    }

    get csvFileName(): string {
      return 'lockers_install_export.csv';
    }

    csvMapRow(item: IShortLocker): Array<string | number | null | undefined> {
      return [
        item.id,
        item.name || '',
        item.community || '',
        item.machine_number != null ? item.machine_number : '',
        item.version || '',
        item.os_version || '',
        item.os_cpu || '',
        item.os_memory || '',
        item.status || '',
        (item as any).serviceProviderCount != null ? (item as any).serviceProviderCount : '',
        (item as any).screensaverCount != null ? (item as any).screensaverCount : '',
        item.last_ping || '',
      ];
    }

    fetchLockersCsv(): Promise<any[]> {
      const filters: any = {};
      if (this.search) filters.search = this.search;
      if (this.showAll) filters.show_deleted = 1;
      filters.sort_by = this.activeSortBy;
      filters.sort_order = this.activeSortOrder;
      return lockerService.exportLockers(filters);
    }

    shortDate(d: string) {
      return d ? d.slice(2, -3) : null;
    }

    private silentFetch() {
      const filters = this.buildFilters();
      filters.page = this.lockersListCurrentPage;
      this.$store.dispatch('lockerModule/fetchLockersList', filters);
    }

    private silentFetchNetworkHealth() {
      lockerService.fetchNetworkHealth().then((data: Record<number, ILockerNetworkHealth>) => {
        this.networkHealthMap = data;
      });
    }

    truncatedItem(item: IShortLocker): IShortLocker {
      return Object.assign({}, item, { name: item.name.slice(0, 19) + '...' });
    }

    statusClicked(item: any) {
      this.search = item.status;
    }

    copyText(text: string): void {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text);
      } else {
        const el = document.createElement('textarea');
        el.value = text;
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
    }

    networkScoreColor(score: number): string {
      if (score >= 4) return 'green darken-1';
      if (score === 3) return 'light-green darken-1';
      if (score === 2) return 'orange darken-1';
      if (score === 1) return 'red darken-1';
      return 'grey';
    }

    networkScoreIcon(score: number): string {
      if (score >= 4) return 'mdi-wifi';
      if (score === 3) return 'mdi-wifi-strength-3';
      if (score === 2) return 'mdi-wifi-strength-2';
      if (score === 1) return 'mdi-wifi-strength-1';
      return 'mdi-wifi-strength-outline';
    }

    versionColor(item: IShortLocker): string {
      if (this.isMaxVersion(item)) {
        return 'green';
      }
      else if (item.status == ELockerStatuses.Online) {
        return 'red';
      }
      else {
        return 'orange';
      }
    }

  }
